import React, { useState } from 'react';
import AutoFillPanel from './AutoFillPanel';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card } from '../../ui/card';
import {
  Plus, Edit, Trash2, X, Clock, Table, ChevronDown, ChevronRight
} from 'lucide-react';
import { API } from '../../../lib/api';

const formatCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

// Converte string no formato brasileiro (ex: "1.200,50") para float JS
const parseBR = (str) => {
  if (str === '' || str == null) return NaN;
  // Remove pontos de milhar, substitui vírgula por ponto
  const normalized = String(str).replace(/\./g, '').replace(',', '.');
  return parseFloat(normalized);
};

// Formata float JS para string no padrão brasileiro sem símbolo de moeda
const formatBR = (v) => {
  if (v === '' || v == null || isNaN(Number(v))) return '';
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(v));
};

const InvestmentTablesTab = ({ tables, getAuthHeaders, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [tableName, setTableName] = useState('');
  const [validityDays, setValidityDays] = useState(30);
  const [ranges, setRanges] = useState([{ label: '', consumption_max: '', investment_value: '', monthly_savings: '' }]);
  const [saving, setSaving] = useState(false);
  const [expandedTable, setExpandedTable] = useState(null);
  const [extendModal, setExtendModal] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [extending, setExtending] = useState(false);

  const resetForm = () => {
    setTableName('');
    setValidityDays(30);
    setRanges([{ label: '', consumption_max: '', investment_value: '', monthly_savings: '' }]);
    setEditingTable(null);
    setShowForm(false);
  };

  const openEdit = (t) => {
    setTableName(t.name);
    setRanges(t.ranges.map(r => ({
      label: r.label,
      consumption_max: formatBR(r.consumption_max),
      investment_value: formatBR(r.investment_value),
      monthly_savings: formatBR(r.monthly_savings),
    })));
    setEditingTable(t);
    setShowForm(true);
  };

  const updateRange = (idx, field, value) => {
    setRanges(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const addRange = () => setRanges(prev => [...prev, { label: '', consumption_max: '', investment_value: '', monthly_savings: '' }]);
  const removeRange = (idx) => setRanges(prev => prev.filter((_, i) => i !== idx));

  const calcReturn = (r) => {
    const inv = parseBR(r.investment_value);
    const savings = parseBR(r.monthly_savings);
    if (inv > 0 && savings >= 0) return ((savings / inv) * 100).toFixed(1).replace('.', ',') + '%';
    return '—';
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!tableName.trim()) { toast.error('Informe o nome da tabela'); return; }
    const validRanges = ranges.filter(r => r.label && r.investment_value && r.monthly_savings);
    if (validRanges.length === 0) { toast.error('Adicione ao menos uma faixa'); return; }
    setSaving(true);
    try {
      const payload = {
        name: tableName,
        ranges: validRanges.map(r => ({
          label: r.label,
          consumption_max: parseBR(r.consumption_max) || 0,
          investment_value: parseBR(r.investment_value),
          monthly_savings: parseBR(r.monthly_savings),
        }))
      };
      if (editingTable) {
        await axios.put(`${API}/investment-tables/${editingTable.id}`, payload, getAuthHeaders());
        toast.success('Tabela atualizada!');
      } else {
        await axios.post(`${API}/investment-tables`, { ...payload, validity_days: validityDays }, getAuthHeaders());
        toast.success('Tabela criada!');
      }
      resetForm();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar tabela');
    } finally {
      setSaving(false);
    }
  };

  const handleExtend = async () => {
    if (!extendModal || extendDays <= 0) return;
    setExtending(true);
    try {
      await axios.post(`${API}/investment-tables/${extendModal.id}/extend`, { days: extendDays }, getAuthHeaders());
      toast.success(`Validade estendida por ${extendDays} dias!`);
      setExtendModal(null);
      setExtendDays(30);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao estender validade');
    } finally {
      setExtending(false);
    }
  };

  const formatValidity = (isoStr) => {
    if (!isoStr) return null;
    const d = new Date(isoStr);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const isExpired = (isoStr) => {
    if (!isoStr) return false;
    return new Date(isoStr) < new Date();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta tabela? Ela não pode estar vinculada a parceiros.')) return;
    try {
      await axios.delete(`${API}/investment-tables/${id}`, getAuthHeaders());
      toast.success('Tabela excluída');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800">Tabelas de Investimento ({tables.length})</h2>
        <Button
          onClick={() => { resetForm(); setShowForm(!showForm); }}
          className="bg-orange-600 hover:bg-orange-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          {showForm && !editingTable ? 'Cancelar' : 'Nova Tabela'}
        </Button>
      </div>

      {/* Extend Validity Modal */}
      {extendModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-500" /> Estender Validade
              </h3>
              <button
                onClick={() => { setExtendModal(null); setExtendDays(30); }}
                className="p-1.5 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-1">
              Tabela: <strong className="text-gray-800">{extendModal.name}</strong>
            </p>
            {extendModal.validity && (
              <p className="text-xs text-gray-500 mb-4">
                Validade atual:{' '}
                <span className={isExpired(extendModal.validity) ? 'text-red-500 font-semibold' : 'text-green-600 font-semibold'}>
                  {formatValidity(extendModal.validity)}{isExpired(extendModal.validity) ? ' (expirada)' : ''}
                </span>
              </p>
            )}
            <label className="text-sm font-medium text-gray-700 mb-1 block">Quantos dias adicionar?</label>
            <input
              type="number" min="1" max="365"
              value={extendDays}
              onChange={(e) => setExtendDays(parseInt(e.target.value) || 1)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 mb-1 text-lg font-semibold text-center"
            />
            <p className="text-xs text-gray-400 text-center mb-5">
              Nova validade:{' '}
              <strong>{(() => {
                const base = extendModal.validity && new Date(extendModal.validity) > new Date()
                  ? new Date(extendModal.validity) : new Date();
                const d = new Date(base);
                d.setDate(d.getDate() + extendDays);
                return d.toLocaleDateString('pt-BR');
              })()}</strong>
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setExtendModal(null); setExtendDays(30); }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 text-sm font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleExtend} disabled={extending}
                className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
              >
                {extending ? 'Salvando...' : `Adicionar ${extendDays} dias`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <Card className="p-6 border-orange-200 bg-orange-50/30">
          <h3 className="font-bold text-gray-800 mb-4">{editingTable ? 'Editar Tabela' : 'Nova Tabela'}</h3>
          <form onSubmit={handleSave} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Nome da Tabela *</label>
                <Input
                  value={tableName}
                  onChange={(e) => setTableName(e.target.value)}
                  required placeholder="Ex: Tabela Padrão Residencial"
                />
              </div>
              {!editingTable && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Validade (dias) *</label>
                  <input
                    type="number" min="1" max="365"
                    value={validityDays}
                    onChange={(e) => setValidityDays(parseInt(e.target.value) || 30)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    A proposta expirará em {validityDays} dias a partir de hoje
                  </p>
                </div>
              )}
            </div>

            <AutoFillPanel
              getAuthHeaders={getAuthHeaders}
              onRangesGenerated={(newRanges) => setRanges(newRanges)}
              currentRanges={ranges}
            />

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Faixas de Consumo *</label>
                <button
                  type="button" onClick={addRange}
                  className="text-xs text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />Adicionar faixa
                </button>
              </div>

              <div className="grid grid-cols-5 gap-2 text-xs font-medium text-gray-500 mb-2 px-1">
                <span className="col-span-1">Label</span>
                <span>Consumo Max. (R$)</span>
                <span>Investimento (R$)</span>
                <span>Economia/mes (R$)</span>
                <span>Retorno %</span>
              </div>

              <div className="space-y-2">
                {ranges.map((r, idx) => (
                  <div key={idx} className="grid grid-cols-5 gap-2 items-center bg-white rounded-xl border border-gray-100 p-2">
                    <Input value={r.label} onChange={(e) => updateRange(idx, 'label', e.target.value)} placeholder="ate R$200" className="text-sm col-span-1" />
                    <Input
                      value={r.consumption_max}
                      onChange={(e) => updateRange(idx, 'consumption_max', e.target.value)}
                      type="text" inputMode="decimal" placeholder="200" className="text-sm"
                    />
                    <Input
                      value={r.investment_value}
                      onChange={(e) => updateRange(idx, 'investment_value', e.target.value)}
                      type="text" inputMode="decimal" placeholder="12.000,00" className="text-sm"
                    />
                    <Input
                      value={r.monthly_savings}
                      onChange={(e) => updateRange(idx, 'monthly_savings', e.target.value)}
                      type="text" inputMode="decimal" placeholder="180,00" className="text-sm"
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-green-600 text-sm font-semibold">{calcReturn(r)}</span>
                      {ranges.length > 1 && (
                        <button type="button" onClick={() => removeRange(idx)} className="p-1 text-red-400 hover:text-red-600 transition-colors">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                O retorno % e calculado automaticamente: (Economia Mensal / Investimento) x 100
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" onClick={resetForm} variant="outline" className="flex-1">Cancelar</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white">
                {saving ? 'Salvando...' : editingTable ? 'Salvar Alterações' : 'Criar Tabela'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <div className="space-y-4">
        {tables.length === 0 && !showForm && (
          <Card className="p-12 text-center">
            <Table className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Nenhuma tabela cadastrada ainda.</p>
          </Card>
        )}
        {tables.map((t) => (
          <Card key={t.id} className="overflow-hidden">
            <div
              className="p-5 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setExpandedTable(expandedTable === t.id ? null : t.id)}
            >
              <div>
                <h3 className="font-bold text-gray-800">{t.name}</h3>
                <div className="flex items-center gap-3 mt-1">
                  <p className="text-sm text-gray-500">{t.ranges?.length || 0} faixas de consumo</p>
                  {t.validity && (
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${isExpired(t.validity) ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-700'}`}>
                      <Clock className="w-3 h-3" />
                      {isExpired(t.validity) ? 'Expirada' : 'Válida'} até {formatValidity(t.validity)}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); setExtendDays(30); setExtendModal(t); }}
                  title="Estender validade"
                  className="p-2 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
                >
                  <Clock className="w-4 h-4 text-amber-600" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); openEdit(t); }}
                  className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <Edit className="w-4 h-4 text-gray-600" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(t.id); }}
                  className="p-2 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
                {expandedTable === t.id
                  ? <ChevronDown className="w-5 h-5 text-gray-400" />
                  : <ChevronRight className="w-5 h-5 text-gray-400" />}
              </div>
            </div>
            {expandedTable === t.id && t.ranges?.length > 0 && (
              <div className="border-t bg-gray-50 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 text-xs border-b">
                      <th className="px-5 py-3 font-medium">Faixa</th>
                      <th className="px-5 py-3 font-medium">Consumo Max.</th>
                      <th className="px-5 py-3 font-medium">Investimento</th>
                      <th className="px-5 py-3 font-medium">Economia/mes</th>
                      <th className="px-5 py-3 font-medium text-green-600">Retorno %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.ranges.map((r, i) => {
                      const pct = r.investment_value > 0 ? ((r.monthly_savings / r.investment_value) * 100).toFixed(1).replace('.', ',') : '0';
                      return (
                        <tr key={i} className="border-b last:border-0 hover:bg-white transition-colors">
                          <td className="px-5 py-3 font-medium text-gray-800">{r.label}</td>
                          <td className="px-5 py-3 text-gray-600">{formatCurrency(r.consumption_max)}</td>
                          <td className="px-5 py-3 text-gray-800 font-semibold">{formatCurrency(r.investment_value)}</td>
                          <td className="px-5 py-3 text-orange-600 font-semibold">{formatCurrency(r.monthly_savings)}</td>
                          <td className="px-5 py-3 font-bold text-green-600">{pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default InvestmentTablesTab;
